/** Which Xero organisation bills each business (ProBound / ProEpoxy / ProTraining) — shown on the Xero Integration page. */
import React, { useCallback, useEffect, useState } from 'react';
import { Building2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { formatApiError } from '@/utils/apiError';

export function XeroBusinessMap({ canEdit, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/xero/linking/overview`, { credentials: 'include' });
    if (res.ok) setData(await res.json());
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const assign = async (business, tenant_id) => {
    try {
      const res = await fetch(`${API_URL}/api/xero/linking/business-map`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ business, tenant_id: tenant_id || null }) });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed');
      toast.success(tenant_id ? 'Xero organisation assigned' : 'Assignment cleared');
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (!data) return null;
  const tenants = data.tenants || [];
  const unmapped = data.businesses.filter((b) => !b.tenant_id).length;
  return (
    <Card className="p-4 sm:p-5" data-testid="xero-business-map">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0"><Building2 className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-900">Which Xero organisation bills each business?</h3>
          <p className="text-xs text-gray-600 mt-0.5">Invoices, quotes and product sync for a business go to the organisation picked here. Connect each Xero login above, then assign it.</p>
        </div>
        {unmapped > 0 ? <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 bg-amber-100 px-2 py-1 rounded-full" data-testid="xero-map-unmapped"><AlertTriangle className="w-3 h-3" /> {unmapped} not assigned</span>
          : <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 bg-emerald-100 px-2 py-1 rounded-full" data-testid="xero-map-complete"><CheckCircle2 className="w-3 h-3" /> All assigned</span>}
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {data.businesses.map((b) => (
          <div key={b.key} className={`rounded-xl border p-3 ${b.tenant_id ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'}`} data-testid={`xero-map-${b.key}`}>
            <div className="font-bold text-gray-900">{b.label}</div>
            <div className="text-[11px] text-gray-500">{b.desc}</div>
            {canEdit ? (
              <select value={b.tenant_id || ''} onChange={(e) => assign(b.key, e.target.value)} className="mt-2 w-full h-9 border rounded-md px-2 bg-white text-sm" data-testid={`xero-map-select-${b.key}`}>
                <option value="">— No Xero org assigned —</option>
                {tenants.map((t) => <option key={t.tenant_id} value={t.tenant_id}>{t.tenant_name || t.tenant_id}</option>)}
              </select>
            ) : <div className="mt-2 text-sm font-semibold">{b.tenant_name || 'No Xero org assigned'}</div>}
            <div className="mt-1 text-[11px] text-gray-600">{b.linked} / {b.total} products linked</div>
          </div>
        ))}
      </div>
      {tenants.length < data.businesses.length && <p className="mt-3 text-[11px] text-gray-500">Only {tenants.length} organisation{tenants.length === 1 ? '' : 's'} connected so far — use <b>Connect another Xero organisation</b> above to add the rest. If the other organisation uses a different Xero login, log out of Xero first (or use a private window) so Xero asks you to sign in again.</p>}
    </Card>
  );
}
